import React, { useCallback, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { FormattedMessage } from 'react-intl';
import { TextInput } from '@doxyme/ui';
import { ReCAPTCHA } from '../../../../../components/recaptcha';
import stripeElementOptions from '../common/stripe-element-options';
import config from '../../../../../global/env.config';
function makeStripePrivateInputsUnfocusable(root) {
    const stripeInputs = root.querySelectorAll('input.__PrivateStripeElement-input[aria-hidden="true"]');
    stripeInputs.forEach((input) => {
        if (input.getAttribute('tabindex') !== '-1') {
            input.setAttribute('tabindex', '-1');
        }
    });
}
export const PaymentForm = ({ captchaRef, onChange, onRecaptchaTokenChange, onSubmit, }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [showBackupZipCode, setShowBackupZipCode] = useState(false);
    const [token, setToken] = useState(null);
    useEffect(() => {
        const container = document.getElementById('card-element');
        if (!container) {
            return;
        }
        makeStripePrivateInputsUnfocusable(container);
        const observer = new MutationObserver(() => {
            makeStripePrivateInputsUnfocusable(container);
        });
        observer.observe(container, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);
    const handleFormSubmit = useCallback((e) => {
        e.preventDefault();
        onSubmit();
    }, [onSubmit]);
    const debouncedOnChange = useDebouncedCallback((postalCode, cardToken) => {
        onChange({
            postalCode,
            cardToken,
        });
    }, 500);
    const onZipCodeBlur = useCallback((e) => {
        const zipCode = e.target.value;
        debouncedOnChange(zipCode, token);
    }, [debouncedOnChange, token]);
    const handleCardElementChange = useCallback(async (event) => {
        if (event.complete) {
            const cardElement = elements.getElement(CardElement);
            const { error, token } = await stripe.createToken(cardElement);
            if (token && token.card.country === 'US' && token.card.address_zip === null) {
                setToken(token);
                setShowBackupZipCode(true);
            }
            else {
                debouncedOnChange(event.value.postalCode, token);
                setShowBackupZipCode(false);
            }
        }
        else {
            setShowBackupZipCode(false);
            debouncedOnChange(null, null);
        }
    }, [elements, stripe, debouncedOnChange]);
    return (<form id="payment-form" onSubmit={handleFormSubmit} noValidate>
      <div className="form-title payment-setup__section-title">
        <FormattedMessage id="auth.payment-setup.cardNumber"/>
      </div>

      <div className="payment-setup__card-element" data-test-id="payment-setup-card-container">
        <CardElement id="card-element" options={stripeElementOptions} onChange={handleCardElementChange}/>
      </div>

      {showBackupZipCode && (<div style={{ marginTop: '7px' }}>
          <div className="form-title payment-setup__section-title">
            <FormattedMessage id="auth.payment-setup.zipcode"/>
          </div>
          <TextInput style={{ width: '100px' }} type="numeric" onBlur={onZipCodeBlur}/>
        </div>)}

      <p id="payment-setup-recaptcha-label" className="payment-setup__recaptcha-label">
        <FormattedMessage id="auth.sign-in.recaptcha" defaultMessage="Confirm you are not a robot"/>
      </p>
      <div id="g-recaptcha" className="payment-setup__recaptcha-container" data-test-id="payment-setup-recaptcha" role="group" aria-labelledby="payment-setup-recaptcha-label">
        <ReCAPTCHA className="react-recaptcha" ref={captchaRef} sitekey={config.googleReCaptchaV2CheckboxSiteKey} tabindex={0} onChange={(token) => {
            onRecaptchaTokenChange(token);
        }} onExpired={() => {
            onRecaptchaTokenChange(null);
        }}/>
      </div>
    </form>);
};
